/**
 * Section 8: static, generic educational strategy reference content, keyed
 * by classic OI regime — `GET /options-dashboard/strategy-insights` (no
 * path param, same content for everyone). Renders the insights for the
 * CURRENTLY ACTIVE regime (from the summary call's
 * `market_summary.current_regime`) prominently, with a "Show all regimes"
 * toggle for the rest of the mapping. The disclaimer is always shown near
 * this section — this is educational reference content, not personalized
 * advice for any user/position.
 */
import { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Grid, Typography } from "@mui/material";
import optionsDashboardService from "@/services/optionsDashboardService";
import type { StrategyInsightsResponse } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { regimeLabel } from "./colors";

function RegimeInsightGrid({ regime, data }: { regime: string; data: StrategyInsightsResponse }) {
  const insights = data.insights_by_regime[regime] ?? [];
  return (
    <Box mb={3}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        {regimeLabel(regime)}
      </Typography>
      {insights.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No strategy insights available for this regime.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {insights.map((insight) => (
            <Grid item xs={12} md={6} key={insight.strategy_name}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {insight.strategy_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Typical condition: {insight.typical_market_condition}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {insight.educational_description}
                  </Typography>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Max Risk
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {insight.maximum_risk}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Max Reward
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {insight.maximum_reward}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export function StrategyInsightsPanel({ currentRegime }: { currentRegime?: string | null }) {
  const [data, setData] = useState<StrategyInsightsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await optionsDashboardService.getStrategyInsights();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(extractErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return <Alert severity="error">{error ?? "Unable to load strategy insights."}</Alert>;
  }

  const otherRegimes = Object.keys(data.insights_by_regime).filter((r) => r !== currentRegime);

  return (
    <Box>
      {currentRegime ? (
        <RegimeInsightGrid regime={currentRegime} data={data} />
      ) : (
        <Typography variant="body2" color="text.secondary" mb={2}>
          No active regime resolved yet — showing all available strategy insights.
        </Typography>
      )}

      {otherRegimes.length > 0 && (
        <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ mb: 2 }}>
          {showAll ? "Hide other regimes" : "Show all regimes"}
        </Button>
      )}

      {showAll && otherRegimes.map((regime) => <RegimeInsightGrid key={regime} regime={regime} data={data} />)}

      <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
        {data.disclaimer_note}
      </Alert>
    </Box>
  );
}

export default StrategyInsightsPanel;
